import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { templateApi, auditApi, authApi } from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import './GovernanceWorkspace.css';

const GovernanceWorkspace = () => {
    const { user, token } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('templates');
    const [templates, setTemplates] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [newUser, setNewUser] = useState({ email: '', username: '', password: '', role: 'operator' });

    useEffect(() => {
        loadData();
    }, [activeTab]);

    const loadData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'templates') {
                const data = await templateApi.list();
                setTemplates(data.templates || []);
            } else if (activeTab === 'audit') {
                const data = await auditApi.list({ limit: 50 });
                setAuditLogs(data.logs || []);
            } else if (activeTab === 'users') {
                const data = await authApi.listUsers();
                setAllUsers(data.users || []);
            }
        } catch (err) {
            console.error('Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async (id) => {
        try {
            await templateApi.activate(id);
            setMessage({ type: 'success', text: 'Template activated for production.' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Activation failed.' });
        }
    };

    const handleRoleUpdate = async (id, newRole) => {
        try {
            await authApi.updateUserRole(id, newRole);
            setMessage({ type: 'success', text: 'User role updated successfully.' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Role update failed.' });
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await authApi.createUser(newUser.email, newUser.username, newUser.password, newUser.role);
            setMessage({ type: 'success', text: 'User created successfully.' });
            setShowCreateUser(false);
            setNewUser({ email: '', username: '', password: '', role: 'operator' });
            loadData();
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Failed to create user.' });
        }
    };

    return (
        <div className="governance-workspace">
            <header className="governance-header">
                <div className="governance-header-content">
                    <h1>Governance Workspace</h1>
                    <p>Manage your calculation templates, monitor activity, and control team access.</p>
                </div>
                <div className="governance-header-actions">
                    <button className="btn btn-secondary" onClick={() => loadData()}>Refresh</button>
                    <button className="btn btn-primary" onClick={() => navigate('/app/upload')}>Upload Template</button>
                </div>
            </header>

            {message && (
                <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-6`}>
                    {message.text}
                </div>
            )}

            {/* Governance Tabs */}
            <div className="governance-tabs">
                {['templates', 'audit', 'users'].map(tab => (
                    <button
                        key={tab}
                        className={`governance-tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="governance-loading">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            ) : (
                <div className="governance-content">
                    {activeTab === 'templates' && (
                        <div className="template-grid">
                            {templates.length === 0 ? (
                                <div className="governance-empty">
                                    <div className="governance-empty-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line x1="16" y1="13" x2="8" y2="13" />
                                            <line x1="16" y1="17" x2="8" y2="17" />
                                            <polyline points="10 9 9 9 8 9" />
                                        </svg>
                                    </div>
                                    <h3 className="governance-empty-title">Let's create your first calculation template</h3>
                                    <p className="governance-empty-description">
                                        Templates turn your Excel spreadsheets into reusable calculators.
                                        Upload an Excel file to get started, and your team can run calculations without touching the formulas.
                                    </p>
                                    <button className="btn btn-primary" onClick={() => navigate('/app/upload')}>
                                        Upload Your First Template
                                    </button>
                                </div>
                            ) : (
                                templates.map(t => (
                                    <div key={t.id} className="template-card">
                                        <div className="template-card-header">
                                            <div className="template-card-title">
                                                <h3>{t.name}</h3>
                                                <span className={`badge ${t.is_active ? 'badge-success' : 'badge-warning'}`}>
                                                    {t.is_active ? 'Active' : 'Draft'}
                                                </span>
                                            </div>
                                            <div className="template-card-actions">
                                                <button
                                                    onClick={() => navigate(`/app/templates/${t.id}/review`)}
                                                    className="btn btn-secondary btn-sm"
                                                >
                                                    {t.is_active ? 'View' : 'Review'}
                                                </button>
                                                {!t.is_active && (
                                                    <button onClick={() => handleActivate(t.id)} className="btn btn-success btn-sm">
                                                        Activate
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <p className="template-card-meta">
                                            Version v{t.version}{t.created_at ? ` • Created ${formatDate(t.created_at)}` : ''}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === 'audit' && (
                        <>
                            {/* Desktop Table */}
                            <div className="mobile-table-container desktop-table">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Timestamp</th>
                                            <th>User</th>
                                            <th>Action</th>
                                            <th>Entity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditLogs.map(log => (
                                            <tr key={log.id}>
                                                <td>{new Date(log.timestamp).toLocaleString()}</td>
                                                <td>
                                                    <div className="user-info">
                                                        <span className="user-name">
                                                            {log.user?.username || `Unknown (${log.user_id?.slice(0, 8)}...)`}
                                                        </span>
                                                        {log.user?.role && (
                                                            <span className={`user-role-badge role-${log.user.role}`}>
                                                                {log.user.role}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td><span className="badge badge-gray">{log.action_type}</span></td>
                                                <td>{log.entity_type} ({log.entity_id?.slice(0, 8)}...)</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="mobile-table-cards">
                                {auditLogs.length === 0 ? (
                                    <div className="governance-empty">
                                        <div className="governance-empty-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="16" y1="13" x2="8" y2="13" />
                                                <line x1="16" y1="17" x2="8" y2="17" />
                                            </svg>
                                        </div>
                                        <h3 className="governance-empty-title">No audit logs found</h3>
                                        <p className="governance-empty-description">Activity will appear here as it happens.</p>
                                    </div>
                                ) : (
                                    auditLogs.map(log => (
                                        <div key={log.id} className="mobile-table-card">
                                            <div className="audit-card-timestamp">
                                                {new Date(log.timestamp).toLocaleString()}
                                            </div>
                                            <div className="audit-card-details">
                                                <div className="audit-detail">
                                                    <div className="audit-detail-label">User</div>
                                                    <div className="audit-detail-value">
                                                        {log.user?.username || `Unknown (${log.user_id?.slice(0, 8)}...)`}
                                                        {log.user?.role && (
                                                            <span className="user-role-badge">{log.user.role}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="audit-detail">
                                                    <div className="audit-detail-label">Action</div>
                                                    <div className="audit-detail-value">
                                                        <span className="badge badge-gray">{log.action_type}</span>
                                                    </div>
                                                </div>
                                                <div className="audit-detail">
                                                    <div className="audit-detail-label">Entity</div>
                                                    <div className="audit-detail-value">
                                                        {log.entity_type} ({log.entity_id?.slice(0, 8)}...)
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === 'users' && (
                        <>
                            {/* Create User Section */}
                            <div className="governance-section-header">
                                <h2>User Management</h2>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowCreateUser(!showCreateUser)}
                                >
                                    {showCreateUser ? 'Cancel' : 'Add User'}
                                </button>
                            </div>

                            {showCreateUser && (
                                <div className="create-user-form">
                                    <form onSubmit={handleCreateUser}>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Email Address</label>
                                                <input
                                                    type="email"
                                                    className="form-input"
                                                    value={newUser.email}
                                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                                    required
                                                    placeholder="user@company.com"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Username</label>
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    value={newUser.username}
                                                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                                    required
                                                    pattern="[a-zA-Z0-9_]+"
                                                    title="Username can only contain letters, numbers, and underscores"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Password</label>
                                                <input
                                                    type="password"
                                                    className="form-input"
                                                    value={newUser.password}
                                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                                    required
                                                    minLength="8"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Role</label>
                                                <select
                                                    className="form-input"
                                                    value={newUser.role}
                                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                                >
                                                    <option value="operator">Operator</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <button type="submit" className="btn btn-primary">
                                                    Create User
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* Desktop Table */}
                            <div className="mobile-table-container desktop-table">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Role</th>
                                            <th>Member Since</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allUsers.map(u => (
                                            <tr key={u.id}>
                                                <td>
                                                    <div className="user-card">
                                                        <div className="user-avatar">
                                                            {u.username.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="user-info">
                                                            <div className="user-name">{u.username}</div>
                                                            <div className="user-id">{u.id.slice(0, 12)}...</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge ${u.role === 'admin' ? 'badge-danger' : 'badge-primary'}`}>
                                                        {u.role}
                                                    </span>
                                                </td>
                                                <td>{formatDate(u.created_at)}</td>
                                                <td>
                                                    <select
                                                        className="form-input user-role-select"
                                                        value={u.role}
                                                        onChange={(e) => handleRoleUpdate(u.id, e.target.value)}
                                                        disabled={u.id === user.id}
                                                    >
                                                        <option value="operator">Operator</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="mobile-table-cards">
                                {allUsers.length === 0 ? (
                                    <div className="governance-empty">
                                        <div className="governance-empty-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                <circle cx="12" cy="7" r="4" />
                                            </svg>
                                        </div>
                                        <h3 className="governance-empty-title">No users found</h3>
                                        <p className="governance-empty-description">Users will appear here when they join.</p>
                                    </div>
                                ) : (
                                    allUsers.map(u => (
                                        <div key={u.id} className="mobile-table-card">
                                            <div className="mobile-table-card-header">
                                                <div className="user-card">
                                                    <div className="user-avatar">
                                                        {u.username.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="user-info">
                                                        <div className="user-name">{u.username}</div>
                                                        <div className="user-id">{u.id.slice(0, 12)}...</div>
                                                    </div>
                                                </div>
                                                <span className={`badge ${u.role === 'admin' ? 'badge-danger' : 'badge-primary'}`}>
                                                    {u.role}
                                                </span>
                                            </div>
                                            <div className="mobile-table-card-meta">
                                                <div className="mobile-table-card-row">
                                                    <span className="mobile-table-card-label">Member Since</span>
                                                    <span className="mobile-table-card-value">
                                                        {formatDate(u.created_at)}
                                                    </span>
                                                </div>
                                                <div className="mobile-table-card-row">
                                                    <span className="mobile-table-card-label">Role</span>
                                                    <select
                                                        className="form-input user-role-select"
                                                        value={u.role}
                                                        onChange={(e) => handleRoleUpdate(u.id, e.target.value)}
                                                        disabled={u.id === user.id}
                                                    >
                                                        <option value="operator">Operator</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default GovernanceWorkspace;
